// 🖥️ PC에서 찾은 케이스 — 웹 주도 지그 구조 (2026-10-30, docs/계획_지그_웹주도구조_2026-10-30.md §1·§3)
//   각 PC 의 덴클 에이전트가 agentStatus/{labId}/priv/cases_{deviceId} 에 올린 케이스 목록을 onSnapshot 으로 읽어 PC 별 카드로 보인다.
//   여기서 고른 케이스를 [만들기](후보·실패 = 생성, 완성 = 다시 만들기; 범위 드롭다운 지그/지그+모델, 기본 지그)·[PC 훑기] 로 보내면
//   (대표 2026-11-04: 회색 [지그 생성]·[지그+모델]·[후보 지우기] 버튼 제거, 기간(최근 N일) 드롭다운 추가 — 에이전트 alpha.124 부터 recentDays 명령)
//   agentStatus/{labId}/priv/commands_{deviceId} 에 setDoc(merge) 로 명령을 넣고, 에이전트가 pending → done/error 로 갱신한 것을 같은 문서에서 다시 읽어 보인다.
//   서버 함수 없음·규칙 변경 없음(회사 계정만 priv/* 읽고 쓴다). 2026-10-30 대표 결정: label 은 케이스 폴더명 그대로(환자 이름 포함 가능) — 검색란이 label 도 찾는다.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { RefreshCw } from 'lucide-react';
import { pcDropsModel, dropModelText } from '../utils/agentModelSupport';   // 옛 덴클 프로그램이 모델을 빼는지 (2026-11-02)

// 케이스 상태 배지 (§1-1 status)
const CASE_STATUS = {
    candidate: { label: '후보', color: '#64748b', bg: '#f1f5f9' },
    pending: { label: '보내는 중', color: '#2563eb', bg: '#eff6ff' },
    uploading: { label: '보내는 중', color: '#2563eb', bg: '#eff6ff' },
    waiting: { label: '만드는 중', color: '#7c3aed', bg: '#f5f3ff' },
    ready: { label: '완성', color: '#0f766e', bg: '#f0fdfa' },
    delivered: { label: '완성', color: '#0f766e', bg: '#f0fdfa' },
    error: { label: '실패', color: '#dc2626', bg: '#fef2f2' },
    remade: { label: '다시 만듦', color: '#92400e', bg: '#fffbeb' },
};
const SELECTABLE = new Set(['candidate', 'error', 'delivered']);
const REMAKABLE = new Set(['delivered', 'ready', 'error']);
const CMD_LABEL = { generate: '지그 생성', remake: '다시 만들기', cancel: '후보 지우기', rescan: 'PC 훑기', recentDays: '기간' };
const RECENT_DAYS_CHOICES = [1, 3, 7, 14];   // 에이전트 jig-roundtrip RECENT_DAYS_CHOICES 와 같다
const SCOPE_LABEL = { jig: '지그', both: '지그+모델', model: '모델' };

/** '2분 전' 같은 상대 시각. 숫자가 아니면 '—' */
const CMD_STALE_MS = 10 * 60 * 1000;   // 대기 명령이 이보다 오래되면 '응답 없음'(잠금 해제). PC 는 켜져 있으면 몇 초 안에 받는다

export function relTime(ms, now = Date.now()) {
    if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return '—';
    const d = Math.max(0, now - ms);
    if (d < 60 * 1000) return '방금';
    if (d < 60 * 60 * 1000) return `${Math.floor(d / 60000)}분 전`;
    if (d < 24 * 60 * 60 * 1000) return `${Math.floor(d / 3600000)}시간 전`;
    return `${Math.floor(d / 86400000)}일 전`;
}

/** 명령 id — crypto.randomUUID 가 없는 오래된 브라우저(비보안 컨텍스트)는 시간+난수로 대신 */
function newCmdId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 한 PC 의 케이스 목록 문서 → 정렬된 행 배열 (후보 먼저, 그다음 maxMtimeMs 내림차순) */
function sortCases(cases) {
    const arr = Object.entries(cases || {}).map(([caseKey, c]) => ({ caseKey, ...(c || {}) }));
    arr.sort((a, b) => {
        const ca = a.status === 'candidate' ? 0 : 1;
        const cb = b.status === 'candidate' ? 0 : 1;
        if (ca !== cb) return ca - cb;
        return (Number(b.maxMtimeMs) || 0) - (Number(a.maxMtimeMs) || 0);
    });
    return arr;
}

/**
 * props: labId, modelOpen(허용 기공소만 모델 버튼), deviceNames('PC 관리' 이름표 deviceId→이름), resultJobIds(Set — 결과표에 있는 jobId, '결과 ↓' 표시용)
 */
export default function JigPcCases({ labId, modelOpen, features = null, onDevices, deviceNames = {}, resultJobIds }) {
    const onDevicesRef = useRef(onDevices);   // 구독을 다시 걸지 않게 최신 콜백만 ref 로
    onDevicesRef.current = onDevices;
    const [docs, setDocs] = useState([]);          // cases_* 문서들 [{ deviceId, deviceName, version, updatedAt, count, cases }]
    const [cmdDocs, setCmdDocs] = useState({});    // deviceId → commands_{deviceId}.items
    const [loadErr, setLoadErr] = useState('');
    const [filter, setFilter] = useState('');
    const [now, setNow] = useState(Date.now());
    // 2026-10-30 대표: PC 카드를 한꺼번에 다 펼치지 말고, 드롭다운에서 고른 PC 하나만 보이게. 고른 PC 는 이 브라우저에 기억
    const PICK_KEY = `jigPcPick_${labId || ''}`;
    const [picked, setPicked] = useState(() => { try { return localStorage.getItem(`jigPcPick_${labId || ''}`) || ''; } catch { return ''; } });
    const pick = (id) => { setPicked(id); try { localStorage.setItem(PICK_KEY, id); } catch { /* 저장 못 해도 이번엔 보인다 */ } };

    // 상대 시각 갱신(30초)
    useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);

    // priv 컬렉션 구독 — cases_* 와 commands_* 를 한 번에 받아 클라이언트에서 가른다(문서 수가 PC 수 ×2 라 작다)
    useEffect(() => {
        if (!labId) return undefined;
        setLoadErr('');
        const unsub = onSnapshot(collection(db, 'agentStatus', labId, 'priv'), (snap) => {
            const cases = [];
            const cmds = {};
            snap.docs.forEach((d) => {
                const id = d.id;
                if (id.startsWith('cases_')) {
                    const x = d.data() || {};
                    cases.push({ deviceId: x.deviceId || id.slice(6), deviceName: x.deviceName || '', version: x.version || '', updatedAt: x.updatedAt, count: x.count, cases: x.cases || {}, jigOptions: x.jigOptions || null, recentDays: Number.isFinite(Number(x.recentDays)) && Number(x.recentDays) > 0 ? Number(x.recentDays) : null });   // jigOptions: PC 지그 조정값(alpha.119~, 보기만 — 10-30) · recentDays: 기간(alpha.124~) — 11-05 빠져 있어 드롭다운이 안 보였다
                } else if (id.startsWith('commands_')) {
                    cmds[id.slice(9)] = (d.data() || {}).items || {};
                }
            });
            cases.sort((a, b) => (Number(b.updatedAt) || 0) - (Number(a.updatedAt) || 0));
            setDocs(cases);
            if (onDevicesRef.current) onDevicesRef.current(cases.map((c) => ({ deviceId: c.deviceId, version: c.version })));   // 결과표 「모델 빠짐」 표시용(11-02)
            setCmdDocs(cmds);
        }, (e) => {
            console.error('[지그·PC 목록] agentStatus priv 구독 실패:', e?.code || e?.message || e);
            setLoadErr('PC 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        });
        return () => unsub();
    }, [labId]);

    if (!labId) return null;
    if (loadErr) return <div style={S.hint}>{loadErr}</div>;
    if (docs.length === 0) return <div style={S.hint}>{'아직 목록을 올린 PC가 없습니다. 덴클 프로그램(에이전트)이 켜져 있고 지그 폴더가 연결되어 있으면 잠시 뒤 여기에 나타납니다.'}</div>;

    const titleOf = (d) => deviceNames[d.deviceId] || d.deviceName || d.deviceId;
    const candOf = (d) => Object.values(d.cases || {}).filter((c) => c && (c.status === 'candidate' || c.status === 'error')).length;
    // 고른 PC 가 목록에 없으면(처음·PC 사라짐) 첫 PC — 최근에 올린 순으로 정렬돼 있다
    const cur = docs.find((d) => d.deviceId === picked) || docs[0];
    return (
        <div style={S.wrap}>
            <div style={S.toolRow}>
                <select value={cur.deviceId} onChange={(e) => pick(e.target.value)} style={S.pcSelect} aria-label="볼 PC 고르기">
                    {docs.map((d) => {
                        const n = candOf(d);
                        return <option key={d.deviceId} value={d.deviceId}>{`${titleOf(d)} — ${Number(d.count) || Object.keys(d.cases || {}).length}건${n ? ` · 후보·실패 ${n}` : ''} · ${relTime(d.updatedAt, now)}`}</option>;
                    })}
                </select>
                <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="표시명 · 치식 검색" style={S.filterInput} />
            </div>
            <DeviceCard key={cur.deviceId} data={cur} items={cmdDocs[cur.deviceId] || {}} labId={labId} modelOpen={modelOpen}
                dropsModel={modelOpen && pcDropsModel(cur.version, features, labId)}
                title={titleOf(cur)} filter={filter} now={now} resultJobIds={resultJobIds} />
        </div>
    );
}

/**
 * 치식 + 임플란트 종류(대표 2026-11-02 "치식 옆에 임플란트 종류도, 스캔바디 해석한 것으로").
 * 에이전트가 올린 implants[{tooth, system}] — system 은 스캔바디 라이브러리를 실제 임플란트 이름으로 바꾼 값(에이전트 121~, 옛 버전은 스캔바디 이름 그대로).
 * 같은 종류끼리 묶음: "26, 27 · Osstem TS / 36 · Straumann BLX". 종류를 모르는 치아는 번호만.
 */
export function teethWithImplants(r) {
    // 대표 11-05 "임플란트 종류만": 보철 종류(Abutment+Crown·DigitalModelDie 등)는 안 보이고 exocad 와 같이 "치식 · 임플란트 종류"만.
    //   3Shape 의뢰서의 모델 주문 항목(DigitalModel*)만 있는 치아(38·18 같은 자리표시)는 치식에서 뺀다 — 에이전트 128 이하가 섞어 올림.
    const items = Array.isArray(r && r.order && r.order.items) ? r.order.items.filter((x) => x && x.tooth) : [];
    const modelOnly = new Set();
    if (items.length) {
        const real = new Set(items.filter((x) => !/^DigitalModel/i.test(String(x.type || ''))).map((x) => String(x.tooth)));
        for (const x of items) { if (!real.has(String(x.tooth))) modelOnly.add(String(x.tooth)); }
    }
    const teeth = Array.isArray(r && r.teeth) ? r.teeth.map(String).filter((t) => !modelOnly.has(t)) : [];
    const imps = Array.isArray(r && r.implants) ? r.implants.filter((i) => i && i.tooth) : [];
    const sysOf = {};
    for (const i of imps) { const sy = String(i.system || '').trim(); if (sy) sysOf[String(i.tooth)] = sy; }
    const all = [...new Set([...teeth, ...Object.keys(sysOf)])].sort();
    if (!all.length) return '';
    const groups = [];
    const plain = [];
    for (const t of all) {
        const sy = sysOf[t];
        if (!sy) { plain.push(t); continue; }
        const g = groups.find((x) => x.sy === sy);
        if (g) g.t.push(t); else groups.push({ sy, t: [t] });
    }
    const parts = groups.map((g) => `${g.t.join(', ')} · ${g.sy}`);
    if (plain.length) parts.push(plain.join(', '));
    return parts.join(' / ');
}

function DeviceCard({ data, items, labId, modelOpen, dropsModel = false, title, filter, now, resultJobIds }) {
    const [sel, setSel] = useState(() => new Set());
    const [remakeScope, setRemakeScope] = useState('jig');   // 범위 드롭다운(기본 지그) — 생성·다시 만들기 공용
    const makeScope = modelOpen ? remakeScope : 'jig';
    const [sendErr, setSendErr] = useState('');

    const rows = useMemo(() => sortCases(data.cases), [data.cases]);
    const visible = useMemo(() => {
        const kw = filter.trim().toLowerCase();
        if (!kw) return rows;
        return rows.filter((r) => [r.label, teethWithImplants(r), r.order && r.order.patient, r.order && r.order.clinic].filter(Boolean).join(' ').toLowerCase().includes(kw));
    }, [rows, filter]);

    // 명령 목록 — 최근 5개, pending 이 하나라도 있으면 버튼 잠금
    const cmdList = useMemo(() => Object.entries(items || {}).map(([id, c]) => ({ id, ...(c || {}) })).sort((a, b) => (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0)), [items]);
    // 대기 명령이 CMD_STALE_MS 넘게 그대로면 PC 가 처리 결과를 못 올린 것 — 잠그지 않는다(11-05 COM01: 590분 전 대기 2건에 기간·만들기·PC 훑기가 계속 회색)
    const isStale = (c) => c.status === 'pending' && (Number(now) || Date.now()) - (Number(c.createdAt) || 0) > CMD_STALE_MS;
    const pendingCmd = cmdList.some((c) => c.status === 'pending' && !isStale(c));
    const recent = cmdList.slice(0, 5);

    // 선택 중 상태별 분류 — 생성은 candidate/error, 다시 만들기는 delivered/ready/error
    const selected = rows.filter((r) => sel.has(r.caseKey));
    const genKeys = selected.filter((r) => r.status === 'candidate' || r.status === 'error').map((r) => r.caseKey);
    const remakeKeys = selected.filter((r) => REMAKABLE.has(r.status)).map((r) => r.caseKey);

    const toggle = (k) => setSel((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
    const toggleAll = () => {
        const sels = visible.filter((r) => SELECTABLE.has(r.status)).map((r) => r.caseKey);
        setSel((prev) => (sels.length && sels.every((k) => prev.has(k)) ? new Set() : new Set(sels)));
    };

    // 명령 보내기 — commands_{deviceId}.items[cmdId] 에 setDoc(merge). 실패는 화면 + console.error (조용한 실패 금지)
    const sendAll = async (cmds) => {
        if (pendingCmd || !cmds.length) return;
        const scope = cmds[0].scope;
        if (scope !== 'jig' && !modelOpen) { window.alert('3D 모델 생성은 준비 중입니다. 지그만 보낼 수 있습니다.'); return; }
        // 옛 덴클 프로그램(alpha.121 이하)은 모델을 조용히 뺀다(11-02) — 미리 알리고, 모델만이면 막는다
        if (scope !== 'jig' && dropsModel) {
            if (scope === 'model') { window.alert(dropModelText(data.version)); return; }
            if (!window.confirm(dropModelText(data.version) + '\n\n지그만이라도 보낼까요?')) return;
        }
        const uid = auth.currentUser?.uid;
        if (!uid) { setSendErr('로그인 정보가 없어 보낼 수 없습니다. 다시 로그인해 주세요.'); return; }
        setSendErr('');
        const items = {};
        const now = Date.now();
        cmds.forEach((c, i) => { items[newCmdId()] = { ...c, createdAt: now + i, by: uid, status: 'pending' }; });
        try {
            await setDoc(doc(db, 'agentStatus', labId, 'priv', 'commands_' + data.deviceId), { items }, { merge: true });
            if (cmds.some((c) => c.type === 'generate' || c.type === 'remake')) setSel(new Set());
        } catch (e) {
            console.error('[지그·PC 목록] 명령 쓰기 실패:', cmds.map((c) => c.type).join(','), e?.code || e?.message || e);
            setSendErr('명령을 보내지 못했습니다: ' + (e?.code || e?.message || e));
        }
    };
    const send = (type, caseKeys, scope, extra = {}) => sendAll([{ type, caseKeys, scope, ...extra }]);
    // [만들기] = 후보·실패는 생성, 완성은 다시 만들기 — 한 번에 보낸다(대표 2026-11-04 버튼 정리)
    const makeKeys = genKeys.length + remakeKeys.length;
    const make = () => sendAll([
        ...(genKeys.length ? [{ type: 'generate', caseKeys: genKeys, scope: makeScope }] : []),
        ...(remakeKeys.length ? [{ type: 'remake', caseKeys: remakeKeys, scope: makeScope }] : []),
    ]);

    const btn = (disabled) => ({ ...S.btn, ...(disabled ? S.btnOff : {}) });
    const teethText = (r) => teethWithImplants(r) || '—';
    // 3Shape 의뢰서 요약 한 줄(에이전트 alpha.127~): 환자 성 · 치과 · 납기 11-21 (대표 11-05)
    const orderText = (r) => {
        const o = r && r.order;
        if (!o) return '';
        const due = /^\d{4}-(\d{2}-\d{2})/.exec(String(o.due || ''));
        return [o.patient, o.clinic, due ? `납기 ${due[1]}` : ''].filter(Boolean).join(' · ');
    };
    const kindText = (r) => {
        const parts = [];
        if (r.custom) parts.push(`커스텀 ${Number(r.abutmentCount) || Number(r.custom) || ''}`.trim());
        if (r.general) parts.push('일반');
        if (r.plaster) parts.push('석고');
        return parts.length ? parts.join(' / ') : '—';
    };

    return (
        <div style={S.card}>
            <div style={S.head}>
                <span style={S.headTitle} title={data.deviceId}>{title}</span>
                {data.version && <span style={S.headDim}>{data.version}</span>}
                {dropsModel && <span style={S.dropWarn} title={dropModelText(data.version)}>{'옛 버전 — 3D 모델 안 만들어짐 · 업데이트 필요'}</span>}
                {data.jigOptions && <span style={S.headDim} title="이 PC 덴클 프로그램의 지그 조정값 — 바꾸려면 PC의 덴클 프로그램 지그 화면에서">{`지그 조정값 · 시멘트 간격 ${data.jigOptions.cementGap}mm · 홀 여유 ${data.jigOptions.holeClearance}mm`}</span>}
                <span style={S.headDim} title={typeof data.updatedAt === 'number' ? new Date(data.updatedAt).toLocaleString('ko-KR') : ''}>{`올림 ${relTime(data.updatedAt, now)}`}</span>
                <span style={S.headDim}>{`${typeof data.count === 'number' ? data.count : rows.length}건`}</span>
                <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* 기간(최근 N일) — 대표 11-05 "기간 필터가 없지?": 늘 보이게. PC 가 기간을 안 보내면(alpha.123 이하) 회색으로 두고 안내 */}
                    <select style={S.sel} value={data.recentDays || 1} disabled={pendingCmd || !data.recentDays} onChange={(e) => send('recentDays', [], 'jig', { days: Number(e.target.value) })}
                        title={data.recentDays ? '이 PC가 최근 며칠 안에 수정된 케이스를 후보로 올릴지' : '이 PC의 덴클 프로그램이 옛 버전(alpha.123 이하)이라 기간을 바꿀 수 없습니다 — 업데이트 뒤 가능'}>
                        {RECENT_DAYS_CHOICES.map((d) => <option key={d} value={d}>{`최근 ${d}일`}</option>)}
                    </select>
                    {modelOpen && (
                        <select style={S.sel} value={remakeScope} onChange={(e) => setRemakeScope(e.target.value)} title="만들 범위(기본 지그)">
                            <option value="jig">{'지그'}</option>
                            <option value="both">{'지그+모델'}</option>
                        </select>
                    )}
                    <button type="button" style={btn(pendingCmd || makeKeys === 0)} disabled={pendingCmd || makeKeys === 0} onClick={make} title="선택한 후보·실패 케이스는 새로 만들고, 완성 케이스는 새 작업으로 다시 만듭니다(사용량에 다시 셉니다)">{`만들기${makeKeys ? ` (${makeKeys})` : ''}`}</button>
                    <button type="button" style={btn(pendingCmd)} disabled={pendingCmd} onClick={() => send('rescan', [], 'jig')} title="이 PC가 폴더를 다시 훑고 목록을 새로 올립니다"><RefreshCw size={12} />{'PC 훑기'}</button>
                </span>
            </div>
            {sendErr && <div style={S.err}>{sendErr}</div>}

            {visible.length === 0 ? (
                <div style={S.emptyRow}>{filter.trim() ? '검색 결과가 없습니다.' : '이 PC에 올라온 케이스가 없습니다.'}</div>
            ) : (
                <table style={S.table}>
                    <thead>
                        <tr>
                            <th style={{ ...S.th, width: 30 }}><input type="checkbox" onChange={toggleAll} checked={visible.some((r) => SELECTABLE.has(r.status)) && visible.filter((r) => SELECTABLE.has(r.status)).every((r) => sel.has(r.caseKey))} title="선택할 수 있는 줄 전체" /></th>
                            <th style={S.th}>{'표시명'}</th>
                            <th style={S.th}>{'치식'}</th>
                            <th style={{ ...S.th, width: 110 }}>{'종류'}</th>
                            <th style={{ ...S.th, width: 220 }}>{'상태'}</th>
                            <th style={{ ...S.th, width: 90 }}>{'수정'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((r) => {
                            const st = CASE_STATUS[r.status] || { label: r.status || '?', color: '#64748b', bg: '#f1f5f9' };
                            const can = SELECTABLE.has(r.status);
                            const linked = r.jobId && resultJobIds && resultJobIds.has(r.jobId);
                            return (
                                <tr key={r.caseKey} style={r.status === 'error' ? S.trFail : S.tr}>
                                    <td style={S.td}><input type="checkbox" disabled={!can} checked={sel.has(r.caseKey)} onChange={() => toggle(r.caseKey)} /></td>
                                    <td style={S.tdEllip} title={[r.label || r.caseKey, orderText(r)].filter(Boolean).join('\n')}>
                                        {r.label || r.caseKey}{r.kind ? <span style={S.kind}>{r.kind === '3shape' ? '3Shape' : r.kind}</span> : null}
                                        {orderText(r) && <div style={S.orderLine}>{orderText(r)}</div>}
                                    </td>
                                    <td style={S.tdEllip} title={teethText(r)}>{teethText(r)}</td>
                                    <td style={S.td}>{kindText(r)}</td>
                                    <td style={S.td}>
                                        <span style={{ ...S.badge, color: st.color, backgroundColor: st.bg }}>{st.label}</span>
                                        {r.needsCombined && !r.mergedReady && <span style={{ ...S.badge, color: '#9a3412', backgroundColor: '#fff7ed', marginLeft: 4 }} title="CAD에서 합본 STL(장면 내보내기)을 케이스 폴더에 저장해 주세요">{'합본 필요'}</span>}
                                        {r.mergedReady && <span style={{ ...S.badge, color: '#0f766e', backgroundColor: '#f0fdfa', marginLeft: 4 }}>{'합본 있음'}</span>}
                                        {r.model && r.model.status && <span style={{ ...S.badge, color: '#7c3aed', backgroundColor: '#f5f3ff', marginLeft: 4 }} title="3D 모델 상태">{`모델 ${(CASE_STATUS[r.model.status] || {}).label || r.model.status}`}</span>}
                                        {linked && <span style={S.linkMark} title="아래 결과 표에 같은 작업이 있습니다">{'결과 ↓'}</span>}
                                        {r.status === 'error' && r.error && <div style={S.errText} title={String(r.error)}>{String(r.error).slice(0, 60)}</div>}
                                    </td>
                                    <td style={{ ...S.td, whiteSpace: 'nowrap' }} title={typeof r.maxMtimeMs === 'number' ? new Date(r.maxMtimeMs).toLocaleString('ko-KR') : ''}>{relTime(r.maxMtimeMs, now)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {recent.length > 0 && (
                <div style={S.cmdBox}>
                    <div style={S.cmdTitle}>{'최근 명령'}</div>
                    {recent.map((c) => (
                        <div key={c.id} style={S.cmdRow}>
                            <span style={S.cmdName}>{CMD_LABEL[c.type] || c.type}{c.type !== 'rescan' && c.scope ? ` · ${SCOPE_LABEL[c.scope] || c.scope}` : ''}{Array.isArray(c.caseKeys) && c.caseKeys.length ? ` · ${c.caseKeys.length}건` : ''}</span>
                            <span style={S.headDim}>{relTime(c.createdAt, now)}</span>
                            {isStale(c) ? (
                                <span style={{ ...S.badge, color: '#b45309', backgroundColor: '#fffbeb' }} title="PC 가 이 명령의 처리 결과를 올리지 못했습니다. PC 의 덴클 프로그램이 켜져 있는지 확인하고 다시 보내 주세요.">{'응답 없음 — 다시 보내 주세요'}</span>
                            ) : c.status === 'pending' ? (
                                <span style={{ ...S.badge, color: '#2563eb', backgroundColor: '#eff6ff' }}>{'보내는 중…'}</span>
                            ) : c.status === 'done' ? (
                                <span style={{ ...S.badge, color: '#0f766e', backgroundColor: '#f0fdfa' }} title={typeof c.result === 'string' ? c.result : ''}>{'완료' + (c.result ? ` — ${typeof c.result === 'string' ? c.result : JSON.stringify(c.result).slice(0, 80)}` : '')}</span>
                            ) : c.status === 'error' ? (
                                <span style={{ ...S.badge, color: '#dc2626', backgroundColor: '#fef2f2' }} title={String(c.error || '')}>{'실패' + (c.error ? ` — ${String(c.error).slice(0, 80)}` : '')}</span>
                            ) : (
                                <span style={S.headDim}>{c.status || '—'}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

const S = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 10 },
    hint: { fontSize: 12.5, color: '#64748b', padding: '8px 2px' },
    toolRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
    pcSelect: { flex: '0 1 380px', fontSize: 13.5, fontWeight: 600, padding: '7px 10px', border: '1px solid #94a3b8', borderRadius: 8, background: '#fff', color: '#0f172a', cursor: 'pointer' },   // PC 고르기(10-30)
    filterInput: { flex: '0 1 320px', fontSize: 13, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, outline: 'none' },
    card: { backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
    head: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', borderBottom: '1px solid #f1f5f9', backgroundColor: '#f8fafc' },
    headTitle: { fontSize: 14, fontWeight: 700, color: '#1e293b' },
    headDim: { fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' },
    dropWarn: { fontSize: 12, fontWeight: 700, color: '#b45309', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 6, padding: '1px 8px', whiteSpace: 'nowrap' },
    btn: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: '#334155', backgroundColor: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' },
    btnOff: { opacity: 0.5, cursor: 'not-allowed' },
    sel: { fontSize: 12, padding: '4px 4px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' },
    err: { fontSize: 12.5, color: '#991b1b', backgroundColor: '#fef2f2', padding: '6px 14px', borderBottom: '1px solid #fecaca' },
    emptyRow: { fontSize: 12.5, color: '#94a3b8', padding: '12px 14px' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' },
    th: { textAlign: 'left', padding: '8px 12px', color: '#64748b', fontWeight: 600, borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
    tr: { borderBottom: '1px solid #f1f5f9' },
    trFail: { borderBottom: '1px solid #f1f5f9', backgroundColor: '#fef2f2' },
    td: { padding: '8px 12px', color: '#1e293b', verticalAlign: 'middle' },
    tdEllip: { padding: '8px 12px', color: '#1e293b', verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    kind: { marginLeft: 6, fontSize: 11, color: '#94a3b8' },
    orderLine: { fontSize: 11.5, color: '#475569', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' },
    badge: { display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
    linkMark: { marginLeft: 6, fontSize: 11.5, color: '#4338ca', fontWeight: 700, whiteSpace: 'nowrap' },
    errText: { marginTop: 3, fontSize: 11.5, color: '#b91c1c', whiteSpace: 'normal', lineHeight: 1.4 },
    cmdBox: { padding: '8px 14px 10px', borderTop: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 4 },
    cmdTitle: { fontSize: 12, fontWeight: 700, color: '#64748b' },
    cmdRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 },
    cmdName: { color: '#1e293b', fontWeight: 600 },
};
